import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Grid,
  SimpleGrid,
  Text,
  Icon,
  Progress,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import LineChart from "components/Charts/LineChart";
import { canal, tonalite, fmtNombre } from "variables/veille";
import { lineChartOptions } from "variables/charts";
import { useVeilles } from "contexts/VeillesContext";
import { supabase } from "lib/supabase";

const CHART_COLORS = ["teal.400", "teal.400", "teal.300", "teal.300", "teal.200"];

const fmtPortee = (n) => {
  if (!n) return "—";
  if (n >= 1e6) return `${(n / 1e6).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} M`;
  if (n >= 1e3) return `${Math.round(n / 1e3)} k`;
  return fmtNombre(n);
};

// Agrégats réels calculés côté client à partir des mentions ingérées par le moteur.
function useStatsReelles(motsClesSuivis) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let actif = true;
    (async () => {
      const [{ data: mentions }, { data: daily }] = await Promise.all([
        supabase
          .from("mentions")
          .select("canal_id, tonalite, titre, extrait, portee, sources(nom)")
          .limit(3000),
        supabase
          .from("veille_daily_stats")
          .select("jour, mentions, positif, negatif")
          .order("jour"),
      ]);
      if (!actif) return;
      if (!mentions || mentions.length === 0) { setStats({ empty: true }); return; }

      const total = mentions.length;
      const nPos = mentions.filter((m) => m.tonalite === "positif").length;
      const nNeg = mentions.filter((m) => m.tonalite === "negatif").length;
      const portee = mentions.reduce((s, m) => s + (m.portee || 0), 0);

      // Répartition par canal (en % du total)
      const parCanal = {};
      mentions.forEach((m) => { const c = m.canal_id || "web"; parCanal[c] = (parCanal[c] || 0) + 1; });
      const repartition = Object.entries(parCanal)
        .map(([c, n]) => ({ canal: c, valeur: Math.round((n / total) * 100) }))
        .sort((a, b) => b.valeur - a.valeur);

      // Top sources avec tonalité dominante
      const parSource = {};
      mentions.forEach((m) => {
        const nom = (m.sources && m.sources.nom) || "—";
        const s = parSource[nom] || { nom, mentions: 0, pos: 0, neg: 0, canal: m.canal_id };
        s.mentions += 1;
        if (m.tonalite === "positif") s.pos += 1;
        if (m.tonalite === "negatif") s.neg += 1;
        parSource[nom] = s;
      });
      const topSources = Object.values(parSource)
        .sort((a, b) => b.mentions - a.mentions)
        .slice(0, 6)
        .map((s) => ({
          nom: s.nom,
          type: canal(s.canal).label,
          mentions: s.mentions,
          tonalite: s.neg > s.pos ? "negatif" : s.pos > s.neg ? "positif" : "neutre",
        }));

      // Volume par mot-clé suivi (détection simple dans titre + extrait)
      const texte = mentions.map((m) => `${m.titre || ""} ${m.extrait || ""}`.toLowerCase());
      const mots = (motsClesSuivis || [])
        .map((mot) => ({ mot, valeur: texte.filter((t) => t.includes(mot.toLowerCase())).length }))
        .filter((m) => m.valeur > 0)
        .sort((a, b) => b.valeur - a.valeur)
        .slice(0, 5);

      // Série temporelle positif / négatif (agrégat quotidien du moteur)
      const serie = {
        categories: (daily || []).map((d) =>
          new Date(d.jour).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })),
        series: [
          { name: "Positives", data: (daily || []).map((d) => d.positif) },
          { name: "Négatives", data: (daily || []).map((d) => d.negatif) },
        ],
      };

      setStats({
        kpis: [
          { label: "Mentions totales", value: fmtNombre(total) },
          { label: "Portée mesurée", value: fmtPortee(portee) },
          { label: "Sources actives", value: fmtNombre(Object.keys(parSource).length) },
          { label: "Sentiment net", value: `${nPos - nNeg >= 0 ? "+" : ""}${Math.round(((nPos - nNeg) / total) * 100)}` },
        ],
        repartition,
        topSources,
        mots,
        serie,
      });
    })();
    return () => { actif = false; };
  }, [JSON.stringify(motsClesSuivis)]); // eslint-disable-line
  return stats;
}

export default function Statistiques() {
  const textColor = useColorModeValue("gray.700", "white");
  const { veilles } = useVeilles();
  const motsClesSuivis = useMemo(
    () => [...new Set(veilles.flatMap((v) => (v.hasRealData ? v.motsCles : [])))],
    [veilles]
  );
  const reel = useStatsReelles(motsClesSuivis);
  const chargement = reel === null;
  // Données réelles uniquement — aucun repli de démonstration.
  const donnees = reel && !reel.empty ? reel : null;

  const kpis = donnees ? donnees.kpis : [];
  const repartition = donnees ? donnees.repartition : [];
  const topSources = donnees ? donnees.topSources : [];
  const mots = donnees ? donnees.mots : [];
  const maxCanal = Math.max(...repartition.map((r) => r.valeur), 1);
  const maxMot = Math.max(...mots.map((m) => m.valeur), 1);
  const maxSource = Math.max(...topSources.map((s) => s.mentions), 1);

  const chartOptions = donnees
    ? {
        ...lineChartOptions,
        xaxis: { ...lineChartOptions.xaxis, categories: donnees.serie.categories },
        colors: ["#4FD1C5", "#F56565"],
        fill: { ...lineChartOptions.fill, colors: ["#4FD1C5", "#F56565"] },
        legend: { show: true },
      }
    : null;

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Flex align="center" mb="28px">
        <Box w="7px" h="60px" borderRadius="full" bg="teal.300" me="18px" flexShrink={0} />
        <Box>
          <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" color={textColor} lineHeight="1.1">Statistiques</Text>
          <Text fontSize="md" color="gray.700" mt="4px">
            {donnees ? "Analyse transversale de vos veilles · données du moteur." : "Analyse transversale de vos veilles."}
          </Text>
        </Box>
      </Flex>

      {!donnees ? (
        <Card><CardBody><Flex direction="column" align="center" textAlign="center" w="100%" py="70px" px="20px">
          <Text fontWeight="bold" fontSize="lg" color={textColor}>{chargement ? "Chargement des statistiques…" : "Aucune statistique pour l'instant"}</Text>
          {!chargement && <Text fontSize="sm" color="gray.500" mt="6px" maxW="440px">Les indicateurs apparaîtront dès que vos veilles auront collecté leurs premières retombées.</Text>}
        </Flex></CardBody></Card>
      ) : (
      <>
      <Box bg="gray.100" borderRadius="20px" p={{ base: "16px", md: "20px" }} mb="24px">
        <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing="20px">
          {kpis.map((k) => (
            <Card key={k.label}>
              <CardBody p="22px">
                <Flex direction="column" w="100%">
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="0.5px">{k.label}</Text>
                  <Text fontSize="3xl" fontWeight="bold" color="gray.800" mt="8px" lineHeight="1">{k.value}</Text>
                  <Flex align="center" mt="14px" gap="8px">
                    {k.delta !== undefined ? (
                      <>
                        <Flex align="center" bg={k.up ? "green.50" : "red.50"} color={k.up ? "green.500" : "red.500"} px="8px" py="3px" borderRadius="full" fontSize="xs" fontWeight="bold">
                          <Icon as={k.up ? FaArrowUp : FaArrowDown} fontSize="9px" me="3px" />{Math.abs(k.delta)}%
                        </Flex>
                        <Text fontSize="xs" color="gray.500">vs période préc.</Text>
                      </>
                    ) : (
                      <Text fontSize="xs" color="gray.500">sur la période collectée</Text>
                    )}
                  </Flex>
                </Flex>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      </Box>

      <Grid templateColumns={{ base: "1fr", lg: "1.6fr 1fr" }} gap="24px" mb="24px">
        <Card>
          <CardHeader mb="20px">
            <Box>
              <Text fontSize="lg" color={textColor} fontWeight="bold">Tonalité dans le temps</Text>
              <Text fontSize="sm" color="gray.400">Mentions positives et négatives par jour</Text>
            </Box>
          </CardHeader>
          <CardBody h="300px">
            <Box w="100%" h="100%">
              <LineChart series={donnees.serie.series} options={chartOptions} />
            </Box>
          </CardBody>
        </Card>

        <Card>
          <CardHeader mb="16px">
            <Box>
              <Text fontSize="lg" color={textColor} fontWeight="bold">Répartition par canal</Text>
              <Text fontSize="sm" color="gray.400">Part des mentions</Text>
            </Box>
          </CardHeader>
          <CardBody>
            <Flex direction="column" w="100%" gap="14px">
              {repartition.map((r, i) => (
                <Box key={r.canal}>
                  <Flex justify="space-between" mb="4px">
                    <Text fontSize="sm" fontWeight="bold" color={textColor}>{canal(r.canal).label}</Text>
                    <Text fontSize="sm" color="gray.400">{r.valeur}%</Text>
                  </Flex>
                  <Progress value={(r.valeur / maxCanal) * 100} borderRadius="full" size="sm" sx={{ "& > div": { background: `var(--chakra-colors-${(CHART_COLORS[i] || "teal.200").replace(".", "-")})` } }} />
                </Box>
              ))}
            </Flex>
          </CardBody>
        </Card>
      </Grid>

      <Grid templateColumns={{ base: "1fr", lg: "1.6fr 1fr" }} gap="24px">
        <Card>
          <CardHeader mb="12px">
            <Box>
              <Text fontSize="lg" color={textColor} fontWeight="bold">Principales sources</Text>
              <Text fontSize="sm" color="gray.400">Sources les plus actives sur la période</Text>
            </Box>
          </CardHeader>
          <CardBody>
            <Flex direction="column" w="100%">
              {topSources.map((s) => (
                <Flex key={s.nom} align="center" py="10px" borderBottom="1px solid" borderColor="gray.50">
                  <Box flex="1.6"><Text fontSize="sm" fontWeight="bold" color={textColor}>{s.nom}</Text><Text fontSize="xs" color="gray.400">{s.type}</Text></Box>
                  <Box flex="2" px="12px"><Progress value={(s.mentions / maxSource) * 100} colorScheme="teal" borderRadius="full" size="sm" /></Box>
                  <Text flex="0.5" fontSize="sm" fontWeight="bold" color={textColor} textAlign="right">{fmtNombre(s.mentions)}</Text>
                  <Box flex="0.7" textAlign="right"><Text fontSize="xs" fontWeight="bold" color={`${tonalite(s.tonalite).color}.400`}>{tonalite(s.tonalite).label}</Text></Box>
                </Flex>
              ))}
            </Flex>
          </CardBody>
        </Card>

        <Card>
          <CardHeader mb="16px">
            <Box>
              <Text fontSize="lg" color={textColor} fontWeight="bold">Volume par mot-clé</Text>
              <Text fontSize="sm" color="gray.400">Mots-clés suivis détectés dans les mentions</Text>
            </Box>
          </CardHeader>
          <CardBody>
            <Flex direction="column" w="100%" gap="14px">
              {mots.map((m, i) => (
                <Box key={m.mot}>
                  <Flex justify="space-between" mb="4px">
                    <Text fontSize="sm" fontWeight="bold" color={textColor}>{m.mot}</Text>
                    <Text fontSize="sm" color="gray.400">{fmtNombre(m.valeur)}</Text>
                  </Flex>
                  <Progress value={(m.valeur / maxMot) * 100} borderRadius="full" size="sm" sx={{ "& > div": { background: `var(--chakra-colors-${(CHART_COLORS[i] || "teal.200").replace(".", "-")})` } }} />
                </Box>
              ))}
            </Flex>
          </CardBody>
        </Card>
      </Grid>
      </>
      )}
    </Flex>
  );
}
